import { useContext, useEffect, useRef, useState } from 'react'
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material'
import { Alert, Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material'
import { useDebounce } from 'use-debounce'

import { searchAvailableExperiments } from '../../../api/api'
import { AvailableExperimentItem, ExperimentLinkTarget } from '../../../api/models'
import { AppStateContext } from '../../../state/AppProvider'
import { useExperimentManagement } from '../hooks/useExperimentManagement'

import MicroLearningActionModal from './MicroLearningActionModal/MicroLearningActionModal'

type AddExperimentModalProps = {
  open: boolean
  onClose: () => void
  mlId: string
  target: ExperimentLinkTarget
  mlTitle?: string
  onAdded: () => void
}

const MIN_SEARCH_LENGTH = 2

const getTargetLabel = (target: ExperimentLinkTarget) => (target === 'experiments' ? 'Experiments' : 'Scale Potential')

const getExperimentDetails = (item: AvailableExperimentItem) =>
  [item.country, item.ou, item.phase].filter(Boolean).join(' / ')

export default function AddExperimentModal({ open, onClose, mlId, target, mlTitle, onAdded }: AddExperimentModalProps) {
  const appStateContext = useContext(AppStateContext)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<AvailableExperimentItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [debouncedQuery] = useDebounce(query, 300)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dispatchRef = useRef(appStateContext?.dispatch)
  dispatchRef.current = appStateContext?.dispatch
  const { isSubmitting, addExperiment } = useExperimentManagement(mlId, () => undefined)
  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  useEffect(() => {
    if (!open) {
      setQuery('')
      setResults([])
      setIsSearching(false)
      setSearchError(null)
      setHasSearched(false)
      return
    }

    searchInputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return

    const searchTerm = debouncedQuery.trim()

    // Guards against a stale debounce firing on reopen: only act once the
    // debounced value has caught up with the live query (also avoids
    // searching the previous session's term).
    if (searchTerm !== query.trim()) return

    if (searchTerm.length < MIN_SEARCH_LENGTH) {
      setResults([])
      setSearchError(null)
      setHasSearched(false)
      return
    }

    let isActive = true
    setIsSearching(true)
    setSearchError(null)

    searchAvailableExperiments(searchTerm, 15)
      .then(response => {
        if (isActive) {
          setResults(response.items)
          setHasSearched(true)
        }
      })
      .catch(() => {
        if (isActive) {
          setResults([])
          setHasSearched(true)
          const message = 'Could not load experiments. Please try again.'
          setSearchError(message)
          dispatchRef.current?.({
            type: 'SET_SNACKBAR_STATE',
            payload: { message, severity: 'error' },
          })
        }
      })
      .finally(() => {
        if (isActive) {
          setIsSearching(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [debouncedQuery, open, query])

  const handleAdd = async (experimentCode: string) => {
    const success = await addExperiment(experimentCode, target)
    if (success) {
      onAdded()
      setQuery('')
      setResults([])
      onClose()
    }
  }

  const targetLabel = getTargetLabel(target)
  const showMinLengthHint = query.trim().length > 0 && query.trim().length < MIN_SEARCH_LENGTH
  const showNoResults = hasSearched && !isSearching && !searchError && results.length === 0

  return (
    <MicroLearningActionModal
      open={open}
      onClose={handleClose}
      onConfirm={handleClose}
      confirmText='Close'
      confirmDisabled={isSubmitting}
      theme='green'
      headerIcon={<AddIcon fontSize='small' />}
      title={`Add Experiment to ${targetLabel}`}
      subtitle='Search for experiments and add available items'
      mlId={mlId}
      mlTitle={mlTitle}
      maxWidth='md'>
      <TextField
        inputRef={searchInputRef}
        label='Search experiments'
        value={query}
        onChange={event => setQuery(event.target.value)}
        fullWidth
        size='small'
        placeholder='Type an experiment code, country, OU, or phase'
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon fontSize='small' />
            </InputAdornment>
          ),
          endAdornment: isSearching ? <CircularProgress size={18} aria-label='Searching experiments' /> : null,
        }}
      />

      {showMinLengthHint && (
        <Typography variant='body2' color='text.secondary'>
          Type at least {MIN_SEARCH_LENGTH} characters to search.
        </Typography>
      )}

      {searchError && (
        <Alert severity='error' role='alert'>
          {searchError}
        </Alert>
      )}

      {showNoResults && (
        <Typography variant='body2' color='text.secondary'>
          No available experiments match your search.
        </Typography>
      )}

      <Box display='flex' flexDirection='column' gap={1}>
        {results.map(item => {
          const details = getExperimentDetails(item)
          return (
            <Box
              key={item.id ?? item.code}
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              gap={2}
              padding={1.5}
              border='1px solid'
              borderColor={item.available ? 'divider' : 'action.disabledBackground'}
              borderRadius={2}
              bgcolor={item.available ? 'background.paper' : 'action.hover'}>
              <Box minWidth={0}>
                <Typography fontWeight={700}>{item.code}</Typography>
                {details && (
                  <Typography variant='body2' color='text.secondary'>
                    {details}
                  </Typography>
                )}
                {!item.available && (
                  <Typography variant='body2' color='warning.main'>
                    Already linked to {item.linked_ml_id ?? 'another micro-learning'}
                  </Typography>
                )}
              </Box>
              <Button
                variant='contained'
                size='small'
                aria-label={`Add ${item.code}`}
                disabled={!item.available || isSubmitting}
                onClick={() => handleAdd(item.code)}>
                Add
              </Button>
            </Box>
          )
        })}
      </Box>
    </MicroLearningActionModal>
  )
}
